import { useEffect, useState } from 'react';

import ListItemButton from '@mui/material/ListItemButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  GPS_SIDEBAR_ITEMS,
  INSTANCE_SIDEBAR_ITEMS,
  SIDEBAR_TYPE,
} from './constants';
import { StyledLink, StyledSidebarIcon, StyledWarningIcon } from './styles';
import {
  isInstanceRunning,
  isCommunityInstance,
} from '../../../apis/constants';
import { runningNodeExists } from '../../../utils/utils';

const SideBarItem = ({ open, type, item, isAdmin, ec2Instance }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [solrDownWarning, setSolrDownWarning] = useState(null);

  const ec2Running = isInstanceRunning(ec2Instance);
  const solrRunning = runningNodeExists(ec2Instance?.instances);
  const isCommunity = isCommunityInstance(ec2Instance);

  /**
   * Function to check if item should be enabled or disabled.
   * @returns {boolean}
   */
  const isItemEnabled = () => {
    if (type === SIDEBAR_TYPE.INSTANCE) {
      switch (item.name) {
        case INSTANCE_SIDEBAR_ITEMS.COLLECTION:
          return ec2Running && solrRunning;
        case INSTANCE_SIDEBAR_ITEMS.CONFIG:
          return ec2Running && solrRunning;
        case INSTANCE_SIDEBAR_ITEMS.FILE_MANAGER:
          return ec2Running;
        case INSTANCE_SIDEBAR_ITEMS.SEARCH:
          return solrRunning;
        case INSTANCE_SIDEBAR_ITEMS.MONITOR:
          return ec2Running && solrRunning && !isCommunity;
        case INSTANCE_SIDEBAR_ITEMS.HEALTH_CHECK:
          return !isCommunity;
      }
    }
    if (type === SIDEBAR_TYPE.GPS) {
      switch (item.name) {
        case GPS_SIDEBAR_ITEMS.FILE_MANAGER:
          return ec2Running;
        case GPS_SIDEBAR_ITEMS.MONITOR:
          return ec2Running;
      }
    }
    // Default return
    return true;
  };

  // Validations to show warning icon
  useEffect(() => {
    if (isItemEnabled()) {
      setSolrDownWarning(null);
      return;
    }
    if (
      !isCommunity ||
      (item.name !== INSTANCE_SIDEBAR_ITEMS.MONITOR &&
        item.name !== INSTANCE_SIDEBAR_ITEMS.HEALTH_CHECK)
    ) {
      setSolrDownWarning(<StyledWarningIcon open={open} />);
    } else {
      setSolrDownWarning(null);
    }
    // update when the user navigates between instances or sidebars
  }, [ec2Instance, type]);

  if (item.requireAdmin && !isAdmin) return null;

  return (
    <StyledLink to={isItemEnabled() ? item.path : '#'}>
      <ListItemButton
        disabled={!isItemEnabled()}
        aria-label="sidebar-menu"
        sx={{ background: pathname === item.path ? '#f4f4f4' : '' }}
      >
        <StyledSidebarIcon open={open}>{item.icon}</StyledSidebarIcon>
        <ListItemText primary={t(item.name, { ns: 'titles' })} />
        <ListItemSecondaryAction>{solrDownWarning}</ListItemSecondaryAction>
      </ListItemButton>
    </StyledLink>
  );
};

SideBarItem.propTypes = {
  open: PropTypes.bool,
  type: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  ec2Instance: PropTypes.object,
};

export default SideBarItem;
